import { useParams, Link } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { ArrowLeft, Mail, Linkedin, MapPin, Calendar, Briefcase, Award, BadgeCheck } from 'lucide-react';
import { motion } from 'framer-motion';
import { apiFetch } from '../../lib/api';
import { useTranslation } from 'react-i18next';
import { getTeamMemberById, Certification } from '../../config/team';

function XingIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M17.436 3.359a.577.577 0 0 0-.525.311L9.466 16.683l-2.637-4.423a.576.576 0 0 0-.507-.292H3.593a.577.577 0 0 0-.509.855l5.082 8.35a.577.577 0 0 0 .993-.013l9.745-17.058a.577.577 0 0 0-.503-.857h-2.465z"/>
    </svg>
  )
}

export interface TeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  skills?: string[];
  experience?: string;
  email?: string;
  linkedin?: string;
  xing?: string;
  location?: string;
  joinDate?: string;
  achievements?: string[];
  certifications?: Certification[];
  createdAt: string;
  updatedAt: string;
}

// API response type with nested social object
interface ApiTeamMember {
  id: string;
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  skills?: string[];
  experience?: string;
  social?: {
    email?: string;
    linkedin?: string;
    xing?: string;
  };
  createdAt: string;
  updatedAt: string;
}

export default function TeamMemberDetail() {
  const { memberId, lang } = useParams<{ memberId: string; lang?: string }>();
  const { t } = useTranslation('home');
  const { t: tc } = useTranslation('common');
  const [member, setMember] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const currentLang = lang || 'en';

  useEffect(() => {
    const fetchMember = async () => {
      // Get static member for fallback
      const staticMember = getTeamMemberById(memberId || '', currentLang);
      
      try {
        setLoading(true);
        setError(null);

        // Fetch all team members and find the one we need
        const response = await apiFetch('/api/team-members');

        if (!response.ok) {
          throw new Error(`Failed to fetch team member: ${response.statusText}`);
        }

        const data = await response.json();
        const apiMembers: ApiTeamMember[] = data.teamMembers || [];
        const foundApiMember = apiMembers.find(m => m.id === memberId);

        if (!foundApiMember) {
          // Use static member if available
          if (staticMember) {
            setMember(staticMember as TeamMember);
            setError(null);
          } else {
            setError(t('team.memberNotFound'));
          }
          return;
        }

        // Flatten social object and use static image as fallback
        const foundMember: TeamMember = {
          ...foundApiMember,
          email: foundApiMember.social?.email,
          linkedin: foundApiMember.social?.linkedin,
          xing: foundApiMember.social?.xing,
          // Use static image if API doesn't provide one or if it's not an absolute URL
          imageUrl: (foundApiMember.imageUrl && foundApiMember.imageUrl.startsWith('http'))
            ? foundApiMember.imageUrl
            : staticMember?.imageUrl || foundApiMember.imageUrl,
          // Use certifications from static config (API may not have them)
          certifications: staticMember?.certifications
        };

        setMember(foundMember);
      } catch (err) {
        // Fallback to static config when backend is unavailable
        console.warn('Backend unavailable, falling back to static team config:', err);
        
        if (staticMember) {
          setMember(staticMember as TeamMember);
          setError(null); // Don't show error when using fallback
        } else {
          setError(t('team.memberNotFound'));
        }
      } finally {
        setLoading(false);
      }
    };

    fetchMember();
  }, [memberId, currentLang, t]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">{t('team.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !member) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <p className="text-destructive text-xl mb-4">{error || t('team.loadingError')}</p>
          <Link
            to={`/${currentLang}/#team`}
            className="inline-flex items-center gap-2 text-primary hover:opacity-80 transition-opacity"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('team.backToTeam')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-background/80 dark:bg-background/80 backdrop-blur-md border-b border-border">
        <div className="container mx-auto px-4 py-4">
          <Link
            to={`/${currentLang}/#team`}
            className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
            {t('team.backToTeam')}
          </Link>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-24 pt-32">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-4xl mx-auto"
        >
          {/* Profile Header */}
          <div className="bg-card dark:bg-card/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-border shadow-sm">
            <div className="flex flex-col md:flex-row gap-8 items-start">
              {/* Profile Photo */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="flex-shrink-0"
              >
                <div className="w-48 h-48 rounded-2xl overflow-hidden bg-border p-1">
                  {member.imageUrl ? (
                    <img
                      src={member.imageUrl}
                      alt={member.name}
                      className="w-full h-full object-cover object-top rounded-xl"
                    />
                  ) : (
                    <div className="w-full h-full bg-muted rounded-xl flex items-center justify-center">
                      <span className="text-6xl font-bold text-muted-foreground">
                        {member.name.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
              </motion.div>

              {/* Basic Info */}
              <div className="flex-1">
                <h1 className="text-4xl font-bold text-foreground mb-2">{member.name}</h1>
                <p className="text-xl text-primary font-semibold mb-4">{member.role}</p>

                {member.location && (
                  <div className="flex items-center gap-2 text-muted-foreground mb-4">
                    <MapPin className="w-4 h-4" />
                    <span>{member.location}</span>
                  </div>
                )}

                {member.joinDate && (
                  <div className="flex items-center gap-2 text-muted-foreground mb-6">
                    <Calendar className="w-4 h-4" />
                    <span>{t('team.joined')} {new Date(member.joinDate).toLocaleDateString(currentLang === 'de' ? 'de-DE' : currentLang === 'fr' ? 'fr-FR' : currentLang === 'es' ? 'es-ES' : currentLang === 'pt' ? 'pt-PT' : currentLang === 'hi' ? 'hi-IN' : currentLang === 'zh' ? 'zh-CN' : 'en-US', { month: 'long', year: 'numeric' })}</span>
                  </div>
                )}

                {/* Social Links */}
                <div className="flex gap-4">
                  {member.email && (
                    <a
                      href={`mailto:${member.email}`}
                      className="flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 rounded-lg text-foreground transition-colors"
                    >
                      <Mail className="w-4 h-4" />
                      {tc('social.email')}
                    </a>
                  )}
                  {member.linkedin && (
                    <a
                      href={member.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-[#0077b5] hover:bg-[#006396] rounded-lg text-white transition-colors"
                    >
                      <Linkedin className="w-4 h-4" />
                      {tc('social.linkedin')}
                    </a>
                  )}
                  {member.xing && (
                    <a
                      href={member.xing}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-2 px-4 py-2 bg-[#006567] hover:bg-[#005550] rounded-lg text-white transition-colors"
                    >
                      <XingIcon className="w-4 h-4" />
                      {tc('social.xing')}
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Bio Section */}
          {member.bio && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="bg-card dark:bg-card/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-border shadow-sm"
            >
              <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-primary" />
                {t('about.title')}
              </h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">{member.bio}</p>
            </motion.div>
          )}

          {/* Skills Section */}
          {member.skills && member.skills.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              className="bg-card dark:bg-card/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-border shadow-sm"
            >
              <h2 className="text-2xl font-bold text-foreground mb-4">{t('team.skills')}</h2>
              <div className="flex flex-wrap gap-3">
                {member.skills.map((skill, index) => (
                  <span
                    key={index}
                    className="px-4 py-2 bg-primary/10 dark:bg-primary/20 border border-primary/20 dark:border-primary/30 rounded-lg text-foreground font-medium hover:bg-primary/20 dark:hover:bg-primary/30 transition-colors"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </motion.div>
          )}

          {/* Certifications Section */}
          {member.certifications && member.certifications.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className="bg-card dark:bg-card/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-border shadow-sm"
            >
              <h2 className="text-2xl font-bold text-foreground mb-6 flex items-center gap-2">
                <BadgeCheck className="w-6 h-6 text-primary" />
                {t('team.certifications')}
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-6">
                {member.certifications.map((cert, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.3, delay: 0.1 * index }}
                    className="flex flex-col items-center text-center group"
                  >
                    <div className="w-20 h-20 mb-3 rounded-xl overflow-hidden bg-muted/50 dark:bg-muted/30 p-2 flex items-center justify-center transition-transform group-hover:scale-110">
                      <img
                        src={cert.badgeUrl}
                        alt={cert.name}
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                      <div className="w-full h-full items-center justify-center bg-primary/20 rounded-lg" style={{ display: 'none' }}>
                        <BadgeCheck className="w-10 h-10 text-primary" />
                      </div>
                    </div>
                    <h3 className="font-semibold text-foreground text-sm">{cert.shortName}</h3>
                    {cert.issuer && (
                      <p className="text-xs text-muted-foreground mt-1">{cert.issuer}</p>
                    )}
                  </motion.div>
                ))}
              </div>
            </motion.div>
          )}

          {/* Experience Section */}
          {member.experience && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              className="bg-card dark:bg-card/50 backdrop-blur-sm rounded-2xl p-8 mb-8 border border-border shadow-sm"
            >
              <h2 className="text-2xl font-bold text-foreground mb-4 flex items-center gap-2">
                <Award className="w-6 h-6 text-primary" />
                {t('team.experience')}
              </h2>
              <p className="text-muted-foreground leading-relaxed whitespace-pre-wrap">{member.experience}</p>
            </motion.div>
          )}

          {/* Achievements Section */}
          {member.achievements && member.achievements.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="bg-card dark:bg-card/50 backdrop-blur-sm rounded-2xl p-8 border border-border shadow-sm"
            >
              <h2 className="text-2xl font-bold text-foreground mb-4">{t('team.achievements')}</h2>
              <ul className="space-y-3">
                {member.achievements.map((achievement, index) => (
                  <li key={index} className="flex items-start gap-3 text-muted-foreground">
                    <span className="flex-shrink-0 w-2 h-2 mt-2 bg-primary rounded-full"></span>
                    <span>{achievement}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
